import { h, Component } from 'preact';
import Location from '../location';
import NMAgentFormRecaptcha from './nm_agent_form_recaptcha.component';
import { jsxIf } from '../utils/jsx.util';
import { isString, isPhoneNumber, isEmail, isZipCode, isEmpty } from '../utils/checking.util';
import ServiceRequest from '../models/service_request.model';
import { directCall, getSCVisitorId } from '../analytics/dtm_helper';
import { newRelicJSError } from '../analytics/new_relic_helper';
import { sanitizePhone } from '../utils/sanitize.util';

/*
NMServiceRequestForm

Properties:

*/
export default class NMServiceRequestForm extends Component {
  constructor() {
    super();

    this.state = {
      serviceRequest: {},
      firstNameError: undefined,
      lastNameError: undefined,
      emailError: undefined,
      phoneError: undefined,
      zipError: undefined,
    };
  }

  componentWillMount () {
    this.getAgentNumber();
    this.getCampaignName();
    this.updateFormFields();
  }

  resetValidations () {
    this.setState({
      firstNameError: undefined,
      lastNameError: undefined,
      emailError: undefined,
      phoneError: undefined,
      zipError: undefined,
      policyOwnerError: undefined,
    });
  }

  validatePolicyOwnerSelected () {
    if (!this.props.policyOwner) {
      this.props.isShowingPolicyOwnerError('Please select if you are currently working with this advisor.');
      return false;
    }

    this.props.isShowingPolicyOwnerError(undefined);
    return true;
  }

  validateFirstName () {
    const firstName = this.state.serviceRequest.first_name;

    if (!isString(firstName)) {
      this.setState({ firstNameError: 'Please enter a valid first name' });
      return false;
    }

    if (firstName.length <= 0) {
      this.setState({ firstNameError: 'Please enter a valid first name' });
      return false;
    }

    return true;
  }

  validateLastName () {
    const lastName = this.state.serviceRequest.last_name;

    if (!isString(lastName)) {
      this.setState({ lastNameError: 'Please enter a valid last name' });
      return false;
    }

    if (lastName.length <= 0) {
      this.setState({ lastNameError: 'Please enter a valid last name' });
      return false;
    }

    return true;
  }

  validateEmail () {
    const { email } = this.state.serviceRequest;

    if (isEmpty(email) || !isEmail(email)) {
      this.setState({ emailError: 'Please enter a valid email address.' });
      return false;
    }

    return true;
  }

  validatePhone () {
    const { phone } = this.state.serviceRequest;

    if (isEmpty(phone) || !isPhoneNumber(phone)) {
      this.setState({ phoneError: 'Please enter a valid phone number.' });
      return false;
    }

    return true;
  }

  validateZipCode () {
    const { zip } = this.state.serviceRequest;

    if (!isString(zip)) {
      this.setState({ zipError: 'Please enter a valid zip code.' });
      return false;
    }

    if (!isZipCode(zip)) {
      this.setState({ zipError: 'Please enter a valid zip code.' });
      return false;
    }

    return true;
  }

  disableSubmitButton () {
    this.submitButton.setAttribute('disabled', '');
  }

  enableSubmitButton () {
    if (this.submitButton) {
      this.submitButton.removeAttribute('disabled', '');
    }
  }

  reCaptchaCallback (recaptchaReponseToken) {
    this.submitServiceRequest(recaptchaReponseToken);
  }

  logError (message, error) {
    newRelicJSError(message, error);
    // console.error(message, error);
  }

  submitServiceRequest (recaptchaReponseToken) {
    const serviceRequest = new ServiceRequest({
      policyowner: 'Yes',
      fldDistLstNo: '1',
      campSrcCde: '1',
    });

    if (Location.getQueryParam('campName')) {
      serviceRequest.campaign_name = Location.getQueryParam('campName');
    } else if (this.props.campaign) {
      serviceRequest.campaign_name = this.props.campaign;
    } else {
      serviceRequest.campaign_name = 'NM.com FR Profile';
    }

    serviceRequest.email = this.props.agentForm.email;
    serviceRequest.first_name = this.props.agentForm.first_name;
    serviceRequest.last_name = this.props.agentForm.last_name;
    serviceRequest.state = this.props.agentForm.state;
    serviceRequest.zip = this.props.agentForm.zip;
    serviceRequest.phone = sanitizePhone(this.props.agentForm.phone);
    serviceRequest.comments = this.props.agentForm.comments;
    serviceRequest.agent_number = this.props.agentNumber;
    serviceRequest.sc_visitor_id = getSCVisitorId();

    serviceRequest.recaptcha_response_token = recaptchaReponseToken;

    serviceRequest.promiseCreate().then(() => {
      this.props.isShowingThankYou(true);
      directCall(`fr-${this.props.formLocation}-submit-service`);
    }).catch((error) => {
      if (this.recaptcha && window.grecaptcha) {
        this.recaptcha.reset();
      }
      this.logError(`Error submitting service request on ${this.props.formLocation}.`, error);
      this.props.isShowingError(true);
    });
  }

  validateForm (event) {
    // Return early if we're already submitting form
    if (this.submitButton && this.submitButton.disabled) {
      return;
    }

    this.disableSubmitButton();

    event.preventDefault();
    event.stopPropagation();

    this.resetValidations();

    const policyOwnerSelectIsValid = this.validatePolicyOwnerSelected();
    const firstNameIsValid = this.validateFirstName();
    const lastNameIsValid = this.validateLastName();
    const zipCodeIsValid = this.validateZipCode();
    const phoneIsValid = this.validatePhone();
    const emailIsValid = this.validateEmail();

    if (policyOwnerSelectIsValid
      && firstNameIsValid
      && lastNameIsValid
      && zipCodeIsValid
      && phoneIsValid
      && emailIsValid) {
      if (this.recaptcha && window.grecaptcha) {
        try {
          // Combine the props.agentForm with the service request state because sometimes the state gets lost with recaptcha
          // In the case of a key collision, the right-most (last) object's value wins out.
          // Meaning our updated attributes will update the current data object.
          this.props.agentForm = { ...this.props.agentForm, ...this.state.serviceRequest };
          this.recaptcha.execute();
        } catch (error) { // if recaptcha execute fails, show error
          this.logError(`Error executing recaptcha on ${this.props.formLocation}.`, error);
          this.props.isShowingError(true);
        }
      } else if (this.props.formLocation === 'dir') {
        this.submitServiceRequest('directory');
      } else { // if no recaptcha on page and not on directory page, show error
        const errorMessage = `Error when attempting to submit service request on ${this.props.formLocation}. No recaptcha on page.`;
        this.logError(errorMessage, new Error(errorMessage));
        this.props.isShowingError(true);
      }
    } else { // if validations failed, reset recaptcha
      if (this.recaptcha && window.grecaptcha) {
        this.recaptcha.reset();
      }
      this.enableSubmitButton();
    }
  }

  handleInputChange (event) {
    const { value, name } = event.target;

    // call dtm each time a form field changes to show form abandonment
    const actionInfo = {
      'action-type': this.props.analyticsFlowNames[name],
      'action-keys': `fr-${this.props.formLocation}-fill-nmx`, // formLocation is either "dir" or "profile"
      'action-values': 'Completed',
    };
    directCall('shared-call', actionInfo);

    this.state.serviceRequest[name] = value;
    this.props.agentForm[name] = value; // update agent form to pass values back and forth between lead and service requests
  }

  getAgentNumber () {
    this.state.serviceRequest.agent_number = this.props.agentNumber;
  }

  getCampaignName () {
    if (Location.getQueryParam('campName')) {
      this.state.serviceRequest.campaign_name = Location.getQueryParam('campName');
    } else if (this.props.campaign) {
      this.state.serviceRequest.campaign_name = this.props.campaign;
    } else {
      this.state.serviceRequest.campaign_name = 'NM.com FR Profile';
    }
  }

  // Update form with values from lead form
  updateFormFields () {
    Object.assign(this.state.serviceRequest, this.props.agentForm);
  }

  render () {
    return (
      <form className='general-form' onSubmit={this.validateForm.bind(this)}>
        <div class='row'>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label>
                First Name
                <sup className='required' aria-required='true'>*</sup>
                <input
                  name='first_name'
                  placeholder='First Name...'
                  type='text'
                  value={this.state.serviceRequest.first_name}
                  onChange={this.handleInputChange.bind(this)}
                />
              </label>
              {jsxIf(this.state.firstNameError, () => (
                <div class='general-form--notice'>{this.state.firstNameError}</div>
              ))}
            </div>
          </div>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label>
                Last Name
                <sup className='required' aria-required='true'>*</sup>
                <input
                  name='last_name'
                  placeholder='Last Name...'
                  type='text'
                  value={this.state.serviceRequest.last_name}
                  onChange={this.handleInputChange.bind(this)}
                />
              </label>
              {jsxIf(this.state.lastNameError, () => (
                <div class='general-form--notice'>{this.state.lastNameError}</div>
              ))}
            </div>
          </div>
        </div>
        <div class='row'>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label>
                Zip Code
                <sup className='required' aria-required='true'>*</sup>
                <input
                  name='zip'
                  placeholder='Zip Code...'
                  type='text'
                  value={this.state.serviceRequest.zip}
                  maxLength='5'
                  onChange={this.handleInputChange.bind(this)}
                />
              </label>
              {jsxIf(this.state.zipError, () => (
                <div class='general-form--notice'>{this.state.zipError}</div>
              ))}
            </div>
          </div>
          <div class='column is-half'>
            <div class='general-form--input'>
              <label>
                Phone Number
                <sup className='required' aria-required='true'>*</sup>
                <input
                  name='phone'
                  placeholder='Phone Number...'
                  type='text'
                  value={this.state.serviceRequest.phone}
                  onChange={this.handleInputChange.bind(this)}
                />
              </label>
              {jsxIf(this.state.phoneError, () => (
                <div class='general-form--notice'>{this.state.phoneError}</div>
              ))}
            </div>
          </div>
        </div>
        <div class='row'>
          <div class='column'>
            <div class='general-form--input'>
              <label>
                Email
                <sup className='required' aria-required='true'>*</sup>
                <input
                  name='email'
                  placeholder='Email..'
                  type='text'
                  value={this.state.serviceRequest.email}
                  onChange={this.handleInputChange.bind(this)}
                />
                {jsxIf(this.state.emailError, () => (
                  <div class='general-form--notice'>{this.state.emailError}</div>
                ))}
              </label>
            </div>
          </div>
        </div>
        <div class='row'>
          <div class='column'>
            <div class='general-form--input'>
              <label>
                Tell us more about your needs...
                <textarea
                  name='comments'
                  placeholder='Comments...'
                  rows='5'
                  maxlength='2500'
                  value={this.state.serviceRequest.comments}
                  onChange={this.handleInputChange.bind(this)} />
              </label>
            </div>
          </div>
        </div>
        <div class='general-form--actions'>
          <button type='submit' class='nmx-button nmx-button--small' value='submit' ref={elm => this.submitButton = elm}>
            Submit
          </button>
        </div>
        {jsxIf(
          this.props.formLocation === 'profile'
          || this.props.formLocation === 'cta',
          () => (
            <NMAgentFormRecaptcha
              recaptchaIds={this.props.recaptchaIds}
              ref={ref => this.recaptcha = ref} // used to reset and execute on this form
              sitekey='<%=recaptchaInvisible%>'
              size='invisible'
              callback={this.reCaptchaCallback.bind(this)}
              recaptchaIdName='serviceRequestId'
              formLocation={this.props.formLocation}
            />
          ),
        )}
      </form>
    );
  }
}
